import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import clsx from "clsx";
import { Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Spinner } from "../../components/common/Spinner";
import { Toggle } from "../../components/common/Toggle";
import {
  getNetwork, patchNetwork, patchP2P,
  type NetworkState, type NetworkPatchPayload,
} from "../../api/config";
import type { Device } from "../../types";

interface Props {
  deviceId: number;
  device: Device;
}

function isValidIPv4(s: string): boolean {
  if (!s) return false;
  const parts = s.split(".");
  if (parts.length !== 4) return false;
  return parts.every(p => {
    const n = Number(p);
    return Number.isInteger(n) && n >= 0 && n <= 255 && String(n) === p;
  });
}

export function NetworkTab({ deviceId, device: _device }: Props) {
  const [state, setState] = useState<NetworkState | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingNet, setSavingNet] = useState(false);
  const [togglingP2P, setTogglingP2P] = useState(false);
  const [copied, setCopied] = useState(false);

  const [hostname, setHostname] = useState("");
  const [domain, setDomain] = useState("");
  const [dhcp, setDhcp] = useState(false);
  const [ip, setIp] = useState("");
  const [mask, setMask] = useState("");
  const [gw, setGw] = useState("");
  const [mtu, setMtu] = useState(1500);
  const [dnsAuto, setDnsAuto] = useState(false);
  const [dns1, setDns1] = useState("");
  const [dns2, setDns2] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const s = await getNetwork(deviceId);
      setState(s);
      setHostname(s.global.hostname);
      setDomain(s.global.domain);
      setDhcp(s.interface.dhcp_enable);
      setIp(s.interface.ip_address);
      setMask(s.interface.subnet_mask);
      setGw(s.interface.default_gateway);
      setMtu(s.interface.mtu);
      setDnsAuto(s.interface.dns_auto_get);
      setDns1(s.interface.dns_primary);
      setDns2(s.interface.dns_secondary);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [deviceId]);

  if (loading || !state) return <Spinner />;

  const save = async () => {
    if (!dhcp) {
      if (!isValidIPv4(ip)) { toast.error("IP inválida"); return; }
      if (!isValidIPv4(mask)) { toast.error("Máscara inválida"); return; }
      if (gw && !isValidIPv4(gw)) { toast.error("Gateway inválido"); return; }
    }
    if (!dnsAuto) {
      if (dns1 && !isValidIPv4(dns1)) { toast.error("DNS primario inválido"); return; }
      if (dns2 && !isValidIPv4(dns2)) { toast.error("DNS secundario inválido"); return; }
    }

    setSavingNet(true);
    try {
      const payload: NetworkPatchPayload = {};
      if (hostname !== state.global.hostname) payload.hostname = hostname;
      if (domain !== state.global.domain) payload.domain = domain;
      if (dhcp !== state.interface.dhcp_enable) payload.dhcp_enable = dhcp;
      if (!dhcp) {
        if (ip !== state.interface.ip_address) payload.ip_address = ip;
        if (mask !== state.interface.subnet_mask) payload.subnet_mask = mask;
        if (gw !== state.interface.default_gateway) payload.default_gateway = gw;
        if (mtu !== state.interface.mtu) payload.mtu = mtu;
      }
      if (dnsAuto !== state.interface.dns_auto_get) payload.dns_auto_get = dnsAuto;
      if (!dnsAuto) {
        if (dns1 !== state.interface.dns_primary) payload.dns_primary = dns1;
        if (dns2 !== state.interface.dns_secondary) payload.dns_secondary = dns2;
      }
      if (Object.keys(payload).length === 0) {
        toast("Sin cambios para guardar");
        return;
      }
      const willChangeIp = !!payload.ip_address && payload.ip_address !== state.interface.ip_address;
      if (willChangeIp) {
        const ok = confirm(`Vas a cambiar la IP del NVR a ${payload.ip_address}. Una vez aplicado, vas a perder conexión hasta actualizar la IP en el listado de dispositivos. ¿Continuar?`);
        if (!ok) return;
      }
      await patchNetwork(deviceId, payload);
      toast.success("Red actualizada");
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSavingNet(false);
    }
  };

  const toggleP2P = async (next: boolean) => {
    if (togglingP2P) return;
    setTogglingP2P(true);
    try {
      await patchP2P(deviceId, next);
      toast.success(next ? "P2P activado" : "P2P desactivado");
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error cambiando P2P");
    } finally {
      setTogglingP2P(false);
    }
  };

  const copySerial = async () => {
    try {
      await navigator.clipboard.writeText(state.p2p.serial);
      setCopied(true);
      toast.success("Serial copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h2 className="text-base font-semibold">Red</h2>
        <p className="text-xs text-muted mt-0.5">
          Configuración de IPv4, DNS, identificación y P2P (Easy4ip) del NVR.
        </p>
      </div>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Identificación</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Nombre del equipo (hostname)" value={hostname} onChange={(e) => setHostname(e.target.value)} maxLength={128} />
          <Input label="Dominio" value={domain} onChange={(e) => setDomain(e.target.value)} maxLength={128} />
        </div>
        <div className="text-xs text-muted">
          Interfaz activa: <span className="font-mono">{state.interface.name}</span>
          <span className="mx-2">·</span>
          MAC: <span className="font-mono">{state.interface.mac || "-"}</span>
          {state.interface.type && (
            <>
              <span className="mx-2">·</span>
              Tipo: <span className="font-mono">{state.interface.type}</span>
            </>
          )}
        </div>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Conexión IPv4</h3>
          <Toggle checked={dhcp} onChange={setDhcp} label={dhcp ? "DHCP activado" : "IP estática"} />
        </div>
        <div className={clsx("grid grid-cols-2 md:grid-cols-4 gap-3", dhcp && "opacity-60")}>
          <Input
            label="Dirección IP"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            disabled={dhcp}
            placeholder="192.168.1.50"
          />
          <Input
            label="Máscara de subred"
            value={mask}
            onChange={(e) => setMask(e.target.value)}
            disabled={dhcp}
            placeholder="255.255.255.0"
          />
          <Input
            label="Puerta de enlace"
            value={gw}
            onChange={(e) => setGw(e.target.value)}
            disabled={dhcp}
            placeholder="192.168.1.1"
          />
          <Input
            label="MTU"
            type="number"
            min={576}
            max={1500}
            value={String(mtu)}
            onChange={(e) => setMtu(Math.max(576, Math.min(1500, Number(e.target.value) || 1500)))}
            disabled={dhcp}
          />
        </div>
        {dhcp && (
          <p className="text-xs text-muted">
            Los valores se obtienen automáticamente del servidor DHCP. Para fijarlos manualmente, desactivá el DHCP.
          </p>
        )}
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">DNS</h3>
          <Toggle
            checked={dnsAuto}
            onChange={setDnsAuto}
            label={dnsAuto ? "Obtener DNS automáticamente" : "DNS manual"}
          />
        </div>
        <div className={clsx("grid grid-cols-2 gap-3", dnsAuto && "opacity-60")}>
          <Input
            label="DNS primario"
            value={dns1}
            onChange={(e) => setDns1(e.target.value)}
            disabled={dnsAuto}
            placeholder="8.8.8.8"
          />
          <Input
            label="DNS secundario"
            value={dns2}
            onChange={(e) => setDns2(e.target.value)}
            disabled={dnsAuto}
            placeholder="8.8.4.4"
          />
        </div>
      </section>

      <div className="flex gap-2">
        <Button variant="primary" onClick={save} loading={savingNet}>Guardar red</Button>
        <Button variant="ghost" onClick={reload} disabled={savingNet}>Restablecer</Button>
      </div>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">P2P (Easy4ip)</h3>
            <p className="text-xs text-muted mt-0.5">
              Acceso remoto vía la nube Dahua para usar la app DMSS / iDMSS Plus desde el celular.
            </p>
          </div>
          {state.p2p.supported && (
            <Toggle
              checked={state.p2p.enable}
              onChange={toggleP2P}
              disabled={togglingP2P}
              label={state.p2p.enable ? "P2P activado" : "P2P desactivado"}
            />
          )}
        </div>

        {!state.p2p.supported ? (
          <div className="text-xs text-yellow-300">
            Este modelo de NVR no expone configuración P2P vía API HTTP.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted w-24 shrink-0">Estado</span>
                <span className={clsx("px-2 py-0.5 rounded border text-xs",
                  state.p2p.enable ? "bg-green-900/40 text-green-300 border-green-700" : "bg-line text-muted border-line",
                )}>
                  {state.p2p.enable ? "Activo" : "Inactivo"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted w-24 shrink-0">Serial (SN)</span>
                <code className="font-mono text-xs bg-line/40 px-2 py-1 rounded flex-1 truncate">{state.p2p.serial || "-"}</code>
                {state.p2p.serial && (
                  <button
                    type="button"
                    onClick={copySerial}
                    className="p-1.5 hover:bg-line rounded text-muted hover:text-text"
                    title="Copiar serial al portapapeles"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted w-24 shrink-0">Tipo</span>
                <span className="font-mono text-xs">{state.p2p.type || "-"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted w-24 shrink-0">Servidor</span>
                <span className="font-mono text-xs">{state.p2p.address || "-"}</span>
              </div>
              <p className="text-xs text-muted pt-2">
                Escaneá el código QR con la app <b>DMSS</b> (Android / iOS) → "Agregar dispositivo" → "Escanear código SN".
              </p>
            </div>
            <div className="flex flex-col items-center justify-center gap-2">
              {state.p2p.serial ? (
                <>
                  <div className="bg-white p-3 rounded">
                    <QRCodeSVG value={state.p2p.serial} size={180} level="M" />
                  </div>
                  <code className="font-mono text-xs text-muted">{state.p2p.serial}</code>
                </>
              ) : (
                <div className="text-xs text-muted italic">Sin serial disponible — QR no se puede generar</div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
